import { Type, type Static } from '@sinclair/typebox';
import {
  SeoBacklinkAcceleratorStatus,
  SeoBacklinkOrigin,
  SeoBacklinkStatus,
  SeoBacklinkTargetFamily,
  SeoKeywordStatus,
  SeoPackPhase,
  SeoProfileGap,
  SeoUnwritableReason,
} from '../translation_namespace/seo_content_namespace';
import { SeoContentType, SeoSearchIntent } from './seo-brief.schema';
import { SeoBacklinkModeSchema } from './seo-settings.schema';

/**
 * API pro `/app/seo/*` (plan §5.5) : sujets du mois à valider, articles à relire.
 * Réservé au plan MAX (scope `website.seo_articles`).
 */

export const SeoTopicResponseSchema = Type.Object({
  seo_keyword_id: Type.Integer(),
  keyword: Type.String(),
  intent: Type.Enum(SeoSearchIntent),
  content_type: Type.Enum(SeoContentType),
  area: Type.Optional(Type.String()),
  score: Type.Number(),
  why_new: Type.String(),
  /** Sujet saisi par le pro (« Ajouter ce sujet ») : le front l'annonce, le motif n'est pas figé en base. */
  is_manual: Type.Boolean(),
  /** Page du site déjà positionnée sur ce sujet : l'article la soutient (lien imposé au brief). */
  support_url: Type.Optional(Type.String()),
  status: Type.Enum(SeoKeywordStatus),
  /** Sujet encore proposé que le pro a dit « oui » (`selected_at`) : il partira au lancement de la rédaction. */
  selected: Type.Boolean(),
  /**
   * Motif d'un « non » : celui du pro (facultatif, modifiable tant que le lot n'est pas lancé), ou le
   * détail en clair d'un refus du moteur quand `unwritable_reason` est posé.
   */
  rejected_reason: Type.Optional(Type.String()),
  /** Sujet écarté par le moteur avant rédaction (porte « écrivable » du brief) : le pourquoi, à libeller. */
  unwritable_reason: Type.Optional(Type.Enum(SeoUnwritableReason)),
  target_month: Type.Optional(Type.String()),
  volume: Type.Optional(Type.Integer()),
  created_at_formatted: Type.Optional(Type.String({ format: 'date-time', meta: { source_key: 'created_at' } })),
});
export type SeoTopicResponseObject = Static<typeof SeoTopicResponseSchema>;

export const SeoTopicsListResponseSchema = Type.Object({
  /** Mois éditorial (`YYYY-MM`). */
  month: Type.String(),
  /** Mois éditorial formaté dans la langue du pro (« novembre 2026 »). */
  month_label: Type.String(),
  /** Jours de publication formatés (« lundi et mercredi »), depuis les réglages du pro. */
  publish_days_label: Type.String(),
  /** Heure locale de publication (0-23), depuis les réglages du pro. */
  publish_hour: Type.Integer(),
  /** Nombre d'articles prévus sur le mois (créneaux × semaines). */
  quota: Type.Integer(),
  /** Créneaux du mois encore libres (quota − articles réservés, programmés, publiés ou en rédaction). */
  remaining: Type.Integer(),
  /**
   * Une recherche de sujets (découverte SERP + volumes + LLM) est en file ou en cours pour ce pro :
   * le bouton « proposer » est désactivé, le moteur ne relancerait pas de toute façon.
   */
  proposal_pending: Type.Boolean(),
  /** Sujets libres saisis par le pro encore en cours d'analyse : affichés dans la liste en attendant. */
  pending_manual: Type.Array(Type.String()),
  /** Budget de découvertes du mois épuisé (`SEO_CLIENT_DISCOVERIES_PER_MONTH_MAX`) : bouton « proposer » masqué. */
  proposal_limit_reached: Type.Boolean(),
  /** Budget de sujets libres du mois épuisé (`SEO_CLIENT_MANUAL_TOPICS_PER_MONTH_MAX`) : saisie masquée. */
  manual_limit_reached: Type.Boolean(),
  /** Sujets proposés auxquels le pro a dit « oui » (pas encore lancés). */
  selected_count: Type.Integer(),
  /** Sujets proposés pas encore tranchés. */
  undecided_count: Type.Integer(),
  /**
   * Le lot peut partir en rédaction (`POST topics/launch`), règle stricte : un « oui » pour chaque
   * date libre du mois et aucune recherche de sujets en cours. Un lot incomplet ne part jamais :
   * le pro demande d'autres sujets (ou le support, budget épuisé) jusqu'à remplir ses dates.
   */
  launchable: Type.Boolean(),
  /** Articles qu'un lancement produit : un par date libre (`remaining`) ; les « oui » en trop sont gardés pour le mois suivant. */
  launch_count: Type.Integer(),
  items: Type.Array(SeoTopicResponseSchema),
});
export type SeoTopicsListResponseObject = Static<typeof SeoTopicsListResponseSchema>;

/**
 * Réponses de `POST topics/propose` : le moteur dédoublonne (une découverte à la fois par pro, un
 * mois déjà garni de propositions ne relance pas), le front distingue les deux cas.
 */
export const SEO_TOPICS_PROPOSAL_MESSAGE = {
  STARTED: 'SEO_TOPICS_PROPOSAL_STARTED',
  ALREADY_PENDING: 'SEO_TOPICS_PROPOSAL_ALREADY_PENDING',
} as const;

const TopicIds = Type.Array(Type.Integer({ minimum: 1 }), { maxItems: 60 });

/**
 * Tri des sujets, enregistré au fil des clics (oui / non, motif, retour en arrière) : rien ne part
 * en rédaction ici, c'est `POST topics/launch` qui lance le lot. Un « non » qui laisse le mois sans
 * assez de candidats déclenche une recherche de complément silencieuse.
 */
export const SeoTopicsDecisionRequestSchema = Type.Object({
  /** Mois éditorial concerné (`YYYY-MM`, défaut : mois suivant). */
  month: Type.Optional(Type.String({ pattern: '^\\d{4}-\\d{2}$' })),
  /** « Oui » : sujets proposés à garder. */
  select: Type.Optional(TopicIds),
  /** Retour sur un « oui ». */
  unselect: Type.Optional(TopicIds),
  /** « Non », avec un motif facultatif (modifiable en renvoyant le même id). */
  reject: Type.Optional(
    Type.Array(
      Type.Object({
        seo_keyword_id: Type.Integer({ minimum: 1 }),
        reason: Type.Optional(Type.String({ maxLength: 300 })),
      }),
      { maxItems: 60 },
    ),
  ),
  /** Retour sur un « non » : le sujet redevient à trancher. */
  unreject: Type.Optional(TopicIds),
  /** Sujet libre proposé par le pro (passe par le même moteur, arrive déjà en « oui »). */
  manual: Type.Optional(Type.Array(Type.String({ minLength: 2, maxLength: 120 }), { maxItems: 5 })),
});
export type SeoTopicsDecisionRequestObject = Static<typeof SeoTopicsDecisionRequestSchema>;

export const SeoTopicsDecidedResponseSchema = Type.Object({
  selected_count: Type.Integer(),
  undecided_count: Type.Integer(),
});
export type SeoTopicsDecidedResponseObject = Static<typeof SeoTopicsDecidedResponseSchema>;

export const SeoTopicsLaunchRequestSchema = Type.Object({
  /** Mois éditorial concerné (`YYYY-MM`, défaut : mois suivant). */
  month: Type.Optional(Type.String({ pattern: '^\\d{4}-\\d{2}$' })),
});
export type SeoTopicsLaunchRequestObject = Static<typeof SeoTopicsLaunchRequestSchema>;

export const SeoTopicsLaunchedResponseSchema = Type.Object({
  /** Sujets partis en rédaction. */
  produced: Type.Integer(),
  /** Sujets (« oui » en trop, ou pas tranchés) gardés pour le mois suivant. */
  carried_over: Type.Integer(),
});
export type SeoTopicsLaunchedResponseObject = Static<typeof SeoTopicsLaunchedResponseSchema>;

export const SeoArticleValidatedResponseSchema = Type.Object({
  seo_article_id: Type.Integer(),
  pro_news_id: Type.Integer(),
  scheduled_publish_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'scheduled_publish_at', with_time: true } }),
  ),
});
export type SeoArticleValidatedResponseObject = Static<typeof SeoArticleValidatedResponseSchema>;

/** Jours de publication par défaut (1 = lundi … 7 = dimanche) et heure locale. */
export const SEO_CLIENT_DEFAULT_PUBLISH_DAYS: readonly number[] = [1, 3];
export const SEO_CLIENT_DEFAULT_PUBLISH_HOUR = 8;
/** Sujets proposés par mois : quota + marge (~8 dates sans l'option « Doubler mes articles »). */
export const SEO_CLIENT_PROPOSALS_PER_MONTH = 12;
/** Même marge de refus avec l'option (~16 dates) — seo-upsell-offers.md §4.4. */
export const SEO_CLIENT_PROPOSALS_PER_MONTH_DOUBLE = 22;

/** Jours de publication par semaine inclus dans l'offre (« 2 articles / semaine ») : quota dur. */
export const SEO_CLIENT_MAX_PUBLISH_DAYS = 2;
/** Avec l'option « Doubler mes articles » (`seo_articles_double`, U2) : 4 articles par semaine. */
export const SEO_CLIENT_MAX_PUBLISH_DAYS_DOUBLE = 4;
/** Jours pré-cochés quand l'option double les articles et que le pro n'en a choisi que deux (U2, §3). */
export const SEO_CLIENT_DOUBLE_DEFAULT_PUBLISH_DAYS: readonly number[] = [1, 2, 3, 5];

/** Plafond de jours de publication et de sujets proposés selon l'option « Doubler mes articles ». */
export function seoClientMaxPublishDays(doubled: boolean): number {
  return doubled ? SEO_CLIENT_MAX_PUBLISH_DAYS_DOUBLE : SEO_CLIENT_MAX_PUBLISH_DAYS;
}
export function seoClientProposalsPerMonth(doubled: boolean): number {
  return doubled ? SEO_CLIENT_PROPOSALS_PER_MONTH_DOUBLE : SEO_CLIENT_PROPOSALS_PER_MONTH;
}

/** Marge minimale d'une recherche de sujets : toujours quelques candidats de plus que les dates à pourvoir. */
export const SEO_CLIENT_PROPOSALS_MIN_MARGIN = 2;

/**
 * Taille d'une recherche de sujets pour `needed` dates encore sans candidat : la moitié en plus,
 * au moins `SEO_CLIENT_PROPOSALS_MIN_MARGIN`, pour que des « non » ne laissent pas de trou (8 dates
 * → 12 sujets ; 3 dates à compléter → 5 sujets).
 */
export function seoClientProposalBatch(needed: number): number {
  if (needed <= 0) return 0;
  return needed + Math.max(SEO_CLIENT_PROPOSALS_MIN_MARGIN, Math.ceil(needed / 2));
}

/**
 * Budget de propositions par mois visé et par pro. Chaque découverte coûte SERP + volumes + LLM et
 * sort des mots-clés NEUFS (les connus sont exclus) : sans plafond, « tout refuser puis redemander »
 * donne une étude de mots-clés illimitée pour un mois d'abonnement. Compté sur `seo_keyword`
 * toutes statuts confondus (refusés compris), donc refuser ne rouvre rien. Au-delà : support.
 */
export const SEO_CLIENT_DISCOVERIES_PER_MONTH_MAX = 3;
/** Sujets libres (« Ajouter ce sujet », une découverte restreinte chacun) par mois visé et par pro. */
export const SEO_CLIENT_MANUAL_TOPICS_PER_MONTH_MAX = 10;

/** État d'un créneau de publication du mois (bandeau « votre pack »). */
export enum SeoSlotState {
  PUBLISHED = 'PUBLISHED',
  SCHEDULED = 'SCHEDULED',
  /** Article rédigé, en attente de validation du pro. */
  TO_REVIEW = 'TO_REVIEW',
  /** Article non validé à temps : son créneau est passé, il glissera au prochain libre. */
  MISSED = 'MISSED',
  /** Créneau passé sans article : perdu (pas de rattrapage, plan §5.5). */
  LOST = 'LOST',
  FREE = 'FREE',
}

export const SeoSlotSchema = Type.Object({
  date_formatted: Type.String({ format: 'date-time', meta: { source_key: 'date', is_short: true } }),
  state: Type.Enum(SeoSlotState),
  pro_news_id: Type.Optional(Type.Integer()),
  title: Type.Optional(Type.String()),
});
export type SeoSlotObject = Static<typeof SeoSlotSchema>;

export const SeoMonthAllocationSchema = Type.Object({
  month: Type.String(),
  month_label: Type.String(),
  /** Créneaux du mois (quota). */
  total: Type.Integer(),
  /** Créneaux pris : publiés, programmés, à valider, en rédaction. */
  used: Type.Integer(),
  /** Créneaux encore disponibles pour de nouveaux sujets. */
  remaining: Type.Integer(),
  lost: Type.Integer(),
  to_review: Type.Integer(),
  /** Sujets validés en cours de rédaction (pas encore de créneau visible). */
  in_production: Type.Integer(),
  slots: Type.Array(SeoSlotSchema),
});
export type SeoMonthAllocationObject = Static<typeof SeoMonthAllocationSchema>;

export const SeoOfferedBacklinkSchema = Type.Object({
  seo_backlink_id: Type.Integer(),
  month: Type.String(),
  month_label: Type.String(),
  source_title: Type.Optional(Type.String()),
  source_url: Type.Optional(Type.String()),
  anchor: Type.Optional(Type.String()),
  target_url: Type.Optional(Type.String()),
  published_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'published_at', is_short: true } }),
  ),
  /** Le lien offert a été retrouvé dans l'index d'un moteur (rapprochement d'URL, plan Bing D2). */
  indexed: Type.Boolean(),
  /** Offre au titre de laquelle le lien a été commandé ; absent pour les anciens liens du blog Propelsia. */
  origin: Type.Optional(Type.Enum(SeoBacklinkOrigin)),
  /** Cycle de vie du lien commandé (linkuma-integration.md §6) ; PUBLISHED pour les anciens liens. */
  status: Type.Enum(SeoBacklinkStatus),
  /** Famille de la page cible (rotation accueil / prestation / actu) ; absent pour les anciens liens. */
  target_family: Type.Optional(Type.Enum(SeoBacklinkTargetFamily)),
  /** La page prévue ne répondait pas : le lien pointe vers l'accueil à la place (§2). */
  target_replaced: Type.Boolean(),
  /** Mode B : titre de l'article à relire et date limite de validation (J+7). */
  article_title: Type.Optional(Type.String()),
  review_due_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'review_due_at', is_short: true } }),
  ),
});
export type SeoOfferedBacklinkObject = Static<typeof SeoOfferedBacklinkSchema>;

/** Contraintes de l'article fourni au partenaire (doc fournisseur, linkuma-integration.md §4.2). */
export const SEO_BACKLINK_ARTICLE_MIN_WORDS = 500;
export const SEO_BACKLINK_ARTICLE_MAX_WORDS = 2500;
export const SEO_BACKLINK_ARTICLE_MAX_LINKS = 2;
export const SEO_BACKLINK_ARTICLE_MIN_H2 = 2;
export const SEO_BACKLINK_ARTICLE_TITLE_MAX = 255;
/** Jours laissés au pro pour valider (mode B) ; sans validation, le mois est perdu. */
export const SEO_BACKLINK_REVIEW_DAYS = 7;
/** Relance à J+3. */
export const SEO_BACKLINK_REVIEW_REMINDER_DAY = 3;

/** Article partenaire à relire (mode B) : lien et ancre verrouillés, titre et corps modifiables. */
export const SeoBacklinkArticleSchema = Type.Object({
  seo_backlink_id: Type.Integer(),
  status: Type.Enum(SeoBacklinkStatus),
  title: Type.String(),
  /** HTML de l'article (sans H1, le titre est à part). */
  html: Type.String(),
  anchor: Type.Optional(Type.String()),
  target_url: Type.String(),
  review_due_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'review_due_at', is_short: true } }),
  ),
  word_count: Type.Integer(),
});
export type SeoBacklinkArticleObject = Static<typeof SeoBacklinkArticleSchema>;

export const SeoBacklinkArticleValidateRequestSchema = Type.Object({
  title: Type.Optional(Type.String({ minLength: 1, maxLength: SEO_BACKLINK_ARTICLE_TITLE_MAX })),
  html: Type.Optional(Type.String({ minLength: 1, maxLength: 60_000 })),
});
export type SeoBacklinkArticleValidateRequestObject = Static<typeof SeoBacklinkArticleValidateRequestSchema>;

export const SeoBacklinkArticleValidatedResponseSchema = Type.Object({
  seo_backlink_id: Type.Integer(),
  status: Type.Enum(SeoBacklinkStatus),
  /** La commande est partie chez le partenaire (sinon elle sera renvoyée automatiquement). */
  ordered: Type.Boolean(),
});
export type SeoBacklinkArticleValidatedResponseObject = Static<typeof SeoBacklinkArticleValidatedResponseSchema>;

/** Accélérateur 3 mois du pro (le plus récent non terminé, sinon le dernier) — onglet Backlinks. */
export const SeoAcceleratorSchema = Type.Object({
  seo_backlink_accelerator_id: Type.Integer(),
  status: Type.Enum(SeoBacklinkAcceleratorStatus),
  links_per_month: Type.Integer(),
  links_total: Type.Integer(),
  links_ordered: Type.Integer(),
  links_published: Type.Integer(),
  /** Premier et dernier mois de la fenêtre, formatés (« novembre 2026 »). */
  starts_month_label: Type.String(),
  ends_month_label: Type.String(),
});
export type SeoAcceleratorObject = Static<typeof SeoAcceleratorSchema>;

/**
 * Offres du pack (seo-upsell-offers.md §3, §4.5) : état des deux options récurrentes et de
 * l'accélérateur, tel que le cycle le lira. Les quantités sont celles du mois SUIVANT (toute
 * activation prend effet au 1er) ; `*_current` dit ce qui est livré ce mois-ci.
 */
export const SeoPackOffersSchema = Type.Object({
  /** Mois d'effet de toute activation faite aujourd'hui, formaté (« novembre 2026 »). */
  effective_month_label: Type.String(),
  /** Articles par semaine ce mois-ci et le mois suivant (2, ou 4 avec l'option). */
  articles_per_week_current: Type.Integer(),
  articles_per_week_next: Type.Integer(),
  /** Option « Doubler mes articles » : active ou programmée (ligne non résiliée). */
  articles_double_active: Type.Boolean(),
  /** Jours de publication maximum autorisés dans les réglages (2, ou 4 si l'option est active ou programmée). */
  max_publish_days: Type.Integer(),
  /** Liens supplémentaires commandés ce mois-ci et le mois suivant (0 à 3). */
  extra_backlinks_current: Type.Integer(),
  extra_backlinks_next: Type.Integer(),
  /** Liens au total le mois suivant : pack + supplémentaires + accélérateur, plafonné. */
  backlinks_per_month_next: Type.Integer(),
  /** Prix mensuel d'un lien supplémentaire, formaté (« 25,00 € »). */
  extra_backlink_unit_price: Type.String({ meta: { is_price: true } }),
  /** Total mensuel pour 1, 2, … liens supplémentaires (index 0 = 1 lien), formaté. */
  extra_backlink_totals: Type.Array(Type.String({ meta: { is_price: true } })),
  /** Option « Liens supplémentaires » résiliée : dernier mois livré, formaté. Absent sinon. */
  extra_backlinks_until_label: Type.Optional(Type.String()),
  /** Option « Doubler mes articles » résiliée : dernier mois livré, formaté. Absent sinon. */
  articles_double_until_label: Type.Optional(Type.String()),
  accelerator: Type.Optional(SeoAcceleratorSchema),
  /** Un nouvel accélérateur peut être acheté (pack actif, aucun accélérateur à venir ou en cours — U13). */
  accelerator_purchasable: Type.Boolean(),
});
export type SeoPackOffersObject = Static<typeof SeoPackOffersSchema>;

/**
 * Lien entrant constaté dans l'index d'un moteur (onglet « Backlinks », seconde carte). Source
 * volontairement non nommée au pro : « d'après les index des moteurs de recherche ».
 */
export const SeoExternalBacklinkSchema = Type.Object({
  source_url: Type.String(),
  source_host: Type.String(),
  anchor: Type.Optional(Type.String()),
  target_url: Type.String(),
  first_seen_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'first_seen_at', is_short: true } }),
  ),
  /** Vu pour la première fois il y a moins de 30 jours. */
  is_new: Type.Boolean(),
  /** Absent des deux dernières synchronisations : le lien a probablement été retiré. */
  is_lost: Type.Boolean(),
});
export type SeoExternalBacklinkObject = Static<typeof SeoExternalBacklinkSchema>;

export const SeoExternalBacklinkHostSchema = Type.Object({
  host: Type.String(),
  /** Liens encore en place depuis cet hôte. */
  links: Type.Integer(),
  first_seen_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'first_seen_at', is_short: true } }),
  ),
});
export type SeoExternalBacklinkHostObject = Static<typeof SeoExternalBacklinkHostSchema>;

/** Nombre de jours pendant lesquels un lien constaté est « nouveau » (et un lien perdu « récent »). */
export const SEO_EXTERNAL_BACKLINK_RECENT_DAYS = 30;
/** Liens renvoyés au pro, au plus (les plus récents d'abord). */
export const SEO_EXTERNAL_BACKLINKS_LIMIT = 200;
/**
 * Backlinks externes (liens reçus constatés par les moteurs) : désactivés le 11/10/2026, l'API
 * publique Bing Webmaster ne les fournit pas (plan `bing-webmaster-backlinks-plan.md` §2). Gate la
 * carte pro et le cron hebdo ; le stockage et l'endpoint restent en place pour une autre source.
 */
export const SEO_EXTERNAL_BACKLINKS_ENABLED = false;

export const SeoExternalBacklinksSchema = Type.Object({
  /**
   * false tant que le site n'est pas vérifié chez le moteur ou qu'aucune synchronisation n'a
   * abouti : la carte affiche « en collecte » plutôt qu'un zéro trompeur.
   */
  verified: Type.Boolean(),
  last_sync_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'last_sync', is_short: true } }),
  ),
  /** Liens encore en place (non perdus). */
  links_total: Type.Integer(),
  /** Hôtes référents distincts parmi les liens en place. */
  domains_total: Type.Integer(),
  new_30d: Type.Integer(),
  lost_30d: Type.Integer(),
  by_host: Type.Array(SeoExternalBacklinkHostSchema),
  links: Type.Array(SeoExternalBacklinkSchema),
});
export type SeoExternalBacklinksObject = Static<typeof SeoExternalBacklinksSchema>;

/**
 * Où en est le pack, pour le dire au pro partout (bandeau du blog, carte du mois, modale des
 * sujets) : les recherches et rédactions durent de longues minutes, il doit toujours savoir ce qui
 * tourne et ce qu'on attend de lui. Une seule phase à la fois, par priorité d'action.
 */
export const SeoPackPhaseSchema = Type.Object({
  kind: Type.Enum(SeoPackPhase),
  /** Mois éditorial concerné, formaté (« novembre 2026 »). */
  month_label: Type.String(),
  /** Avancement (sujets choisis, articles rédigés, articles à valider…) selon la phase. */
  done: Type.Integer(),
  total: Type.Integer(),
});
export type SeoPackPhaseObject = Static<typeof SeoPackPhaseSchema>;

export const SeoPackSummarySchema = Type.Object({
  /** Mois en cours : créneaux et leur état. */
  current: SeoMonthAllocationSchema,
  /** Mois suivant : sujets à choisir. */
  next: SeoMonthAllocationSchema,
  /** Sujets du mois suivant encore proposés (« oui » compris). */
  next_proposed_count: Type.Integer(),
  /** Sujets du mois suivant auxquels le pro a déjà dit « oui ». */
  next_selected_count: Type.Integer(),
  phase: SeoPackPhaseSchema,
  /** Une recherche de sujets est en file ou en cours (cf. `SeoTopicsListResponseSchema.proposal_pending`). */
  next_proposal_pending: Type.Boolean(),
  /** Budget de découvertes du mois suivant épuisé (cf. `SeoTopicsListResponseSchema.proposal_limit_reached`). */
  next_proposal_limit_reached: Type.Boolean(),
  publish_days_label: Type.String(),
  publish_hour: Type.Integer(),
  /**
   * Mois du lien du pack (commandé du 1er au 5, publié dans le mois) : le mois en cours, ou le
   * premier mois du pack (celui qui suit l'activation) tant qu'il n'est pas arrivé.
   */
  backlink_month_label: Type.String(),
  /** Le mois du lien est à venir (pack activé ce mois-ci) : aucun lien n'est dû avant. */
  backlink_month_upcoming: Type.Boolean(),
  /** Un lien du mois en cours est publié. */
  backlink_done: Type.Boolean(),
  /** Un lien du mois en cours est commandé mais pas encore en ligne. */
  backlink_ordered: Type.Boolean(),
  backlinks: Type.Array(SeoOfferedBacklinkSchema),
  offers: SeoPackOffersSchema,
  /**
   * Parcours de première activation terminé (`seo_settings.onboarded_at`). Faux tant que le pro ne
   * l'a pas suivi : le pack est alors en attente (ni sujets, ni lien partenaire), l'espace
   * référencement affiche le parcours à la place des onglets et le bandeau du blog y envoie.
   */
  onboarded: Type.Boolean(),
  /**
   * Ce qui manque au profil pour activer le pack (vide = rien). Calculé tant que le parcours n'est
   * pas suivi : non vide, l'espace référencement affiche « complétez votre profil » à la place du
   * parcours, et `GET` / `POST app/seo/onboarding` refusent (`SEO_PROFILE_INCOMPLETE`).
   */
  profile_gaps: Type.Array(Type.Enum(SeoProfileGap)),
});
export type SeoPackSummaryObject = Static<typeof SeoPackSummarySchema>;

/**
 * Parcours de première activation du pack (`GET app/seo/onboarding`) : ce que le moteur devine
 * déjà, à confirmer par le pro. Les concurrents viennent de la SERP « activité + ville » ; les
 * sites déjà saisis dans les réglages passent devant.
 */
/** Un concurrent proposé : le site, et ce que Google en montre quand il vient de la SERP. */
export const SeoOnboardingCompetitorSchema = Type.Object({
  /** `hôte` ou `hôte/chemin`. */
  site: Type.String(),
  /** Titre et extrait de la page vue sur Google (absents pour un site déjà dans les réglages). */
  title: Type.Optional(Type.String()),
  snippet: Type.Optional(Type.String()),
  /** Requête qui l'a fait remonter (« soin du visage Valbonne »). */
  query: Type.Optional(Type.String()),
});
export type SeoOnboardingCompetitorObject = Static<typeof SeoOnboardingCompetitorSchema>;

export const SeoOnboardingSuggestionsSchema = Type.Object({
  /** Concurrents proposés, au plus `MAX_COMPETITORS` : réglages d'abord, puis SERP. */
  competitors: Type.Array(SeoOnboardingCompetitorSchema),
  /** Zone qui a servi à la recherche (« à Valbonne »), pour le dire au pro. */
  area: Type.Optional(Type.String()),
  /** Mois dont les sujets seront proposés à la fin du parcours (libellé lisible). */
  next_month_label: Type.String(),
});
export type SeoOnboardingSuggestionsObject = Static<typeof SeoOnboardingSuggestionsSchema>;

/**
 * Fin du parcours (`POST app/seo/onboarding`) : les seuls réglages que le pro tranche à
 * l'activation. Le reste (ton, consignes, mots interdits, persona) garde ses défauts et se
 * change dans l'onglet Réglages. Tout est optionnel sauf le mode des articles partenaires :
 * passer le reste sans rien remplir est un choix valide, les défauts du pack s'appliquent.
 */
export const SeoOnboardingRequestSchema = Type.Object({
  author_team_member_id: Type.Optional(Type.Integer({ minimum: 1 })),
  /**
   * Articles partenaires (backlinks) : rédigés et publiés par le partenaire sans relecture (AUTO,
   * vaut acceptation de l'article figé une fois en ligne) ou relus par le pro avant envoi. Exigé
   * ici (décision du 13/10) : rien n'est commandé tant que le pro n'a pas tranché.
   */
  backlink_mode: SeoBacklinkModeSchema,
  /** Sites concurrents retenus, un par entrée (hôte ou URL ; les entrées invalides sont ignorées). */
  competitor_sites: Type.Optional(Type.Array(Type.String({ maxLength: 200 }), { maxItems: 10 })),
  focus_topics: Type.Optional(Type.String({ maxLength: 1500 })),
  excluded_topics: Type.Optional(Type.String({ maxLength: 1500 })),
  /** Jours ISO de publication (1 = lundi … 7 = dimanche), au plus le quota du pack. */
  publish_days: Type.Optional(Type.Array(Type.Integer({ minimum: 1, maximum: 7 }), { maxItems: 7 })),
  publish_hour: Type.Optional(Type.Integer({ minimum: 0, maximum: 23 })),
});
export type SeoOnboardingRequestObject = Static<typeof SeoOnboardingRequestSchema>;

export const SeoOnboardingDoneResponseSchema = Type.Object({
  /** Mois dont les sujets viennent d'être demandés (libellé lisible). */
  month_label: Type.String(),
  /** Faux si une découverte était déjà en file ou si le mois avait déjà ses propositions. */
  proposal_started: Type.Boolean(),
});
export type SeoOnboardingDoneResponseObject = Static<typeof SeoOnboardingDoneResponseSchema>;

/**
 * Un mois de l'espace référencement (onglet « Suivi ») : créneaux et leur état, sujets décidés,
 * backlinks publiés. Mois passés : seulement ceux où il s'est passé quelque chose.
 */
export const SeoTimelineMonthSchema = Type.Object({
  month: Type.String(),
  month_label: Type.String(),
  is_current: Type.Boolean(),
  /** Mois suivant : celui dont le pro choisit les sujets. */
  is_next: Type.Boolean(),
  /** Sujets en attente de décision du pro. */
  topics_proposed: Type.Integer(),
  /** Sujets validés (en rédaction, rédigés ou publiés). */
  topics_validated: Type.Integer(),
  topics_rejected: Type.Integer(),
  allocation: SeoMonthAllocationSchema,
  backlinks: Type.Array(SeoOfferedBacklinkSchema),
});
export type SeoTimelineMonthObject = Static<typeof SeoTimelineMonthSchema>;

export const SeoTimelineResponseSchema = Type.Object({
  /** Du plus récent (mois suivant) au plus ancien. */
  months: Type.Array(SeoTimelineMonthSchema),
});
export type SeoTimelineResponseObject = Static<typeof SeoTimelineResponseSchema>;

/**
 * Résultat Google d'un article publié (onglet « Résultats »), d'après la dernière mesure hebdomadaire
 * de la Search Console. Sans mesure : article trop récent ou pas encore vu par Google.
 */
export const SeoArticleResultSchema = Type.Object({
  seo_article_id: Type.Integer(),
  title: Type.String(),
  keyword: Type.String(),
  published_url: Type.Optional(Type.String()),
  pro_news_id: Type.Optional(Type.Integer()),
  published_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'published_at', is_short: true } }),
  ),
  /** Position moyenne dans Google, arrondie (1 = premier résultat). */
  position: Type.Optional(Type.Integer()),
  /** Places gagnées depuis la mesure précédente (négatif = recul). */
  position_change: Type.Optional(Type.Integer()),
  clicks: Type.Integer(),
  impressions: Type.Integer(),
  measured_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'measured_at', is_short: true } }),
  ),
});
export type SeoArticleResultObject = Static<typeof SeoArticleResultSchema>;

export const SeoResultsResponseSchema = Type.Object({
  /** Fenêtre (jours) sur laquelle la Search Console agrège clics et impressions. */
  window_days: Type.Integer(),
  items: Type.Array(SeoArticleResultSchema),
});
export type SeoResultsResponseObject = Static<typeof SeoResultsResponseSchema>;

/** Demande de prestation supplémentaire (hors pack) : transmise à l'équipe, pas de paiement en ligne. */
/**
 * Résultat d'un lien « en un clic » des e-mails SEO (`POST public/seo/action`), passé à l'app en
 * `?seo_action=` : le backend redirige, le front affiche le toast. Un seul enum pour les deux.
 */
export enum SeoActionResult {
  DONE_TOPICS = 'done_topics',
  /** Sujets passés en « oui » mais lot pas lancé (pas assez de sujets pour les dates libres) : le pro complète dans l'app. */
  TOPICS_SAVED = 'topics_saved',
  DONE_ARTICLES = 'done_articles',
  EXPIRED = 'expired',
  INVALID = 'invalid',
  /** Jeton valide mais l'action a échoué côté serveur : le pro peut réessayer depuis l'app. */
  FAILED = 'failed',
}

export enum SeoUpsellKind {
  ARTICLE = 'ARTICLE',
  BACKLINK = 'BACKLINK',
}

export const SeoUpsellRequestSchema = Type.Object({
  kind: Type.Enum(SeoUpsellKind),
  quantity: Type.Integer({ minimum: 1, maximum: 20 }),
  message: Type.Optional(Type.String({ maxLength: 500 })),
});
export type SeoUpsellRequestObject = Static<typeof SeoUpsellRequestSchema>;
